import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- 1. Global Styles and Layout Setup (Same as before, good for consistency) ---

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
    line-height: 1.6;
    background-color: #fff;
  }

  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Section = styled.section`
  padding: 40px 20px;
  text-align: center;
`;

const PrimaryButton = styled.a`
  display: inline-block;
  background-color: #8cc63f; /* Green color from the original image */
  color: white;
  padding: 12px 30px;
  text-decoration: none;
  font-weight: bold;
  border-radius: 5px;
  transition: background-color 0.3s;
  text-transform: uppercase;
  font-size: 0.9em;

  &:hover {
    background-color: #7ab32e;
  }
`;

// --- 2. Header Section ---

const HeaderSection = styled.header`
  background-color: #222;
  background-image: url('YOUR_MAGNIFYING_GLASS_BACKGROUND_IMAGE_URL'); /* Use your image path */
  background-size: cover;
  background-position: center;
  color: white;
  padding: 100px 20px 150px;
  position: relative;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: 3em;
  font-weight: 700;
  margin-bottom: 30px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
`;

const ContactButton = styled(PrimaryButton)`
  background-color: transparent;
  border: 2px solid white;
  font-size: 1em;
`;

// --- 3. 5 Ways to Increase User Engagement Section (Numbered List) ---

const EngagementSection = styled(Section)`
  padding-top: 80px;
  padding-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-size: 2em;
  font-weight: 700;
  color: #333;
  margin-bottom: 50px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
  margin-bottom: 30px;
  width: 100%;
`;

const FeatureNumber = styled.div`
  font-size: 2em;
  font-weight: bold;
  color: #333;
  margin-right: 20px;
  padding-right: 15px;
  border-right: 2px solid #ccc;
  line-height: 1;
`;

const FeatureContent = styled.div`
  flex: 1;

  h3 {
    font-size: 1.2em;
    font-weight: bold;
    color: #333;
    margin-bottom: 5px;
  }

  p {
    font-size: 0.9em;
    color: #666;
    margin: 0;
  }
`;

// --- 4. Why choose Ghosting Tech? Section (Highlighted Box) ---

const HighlightSection = styled(Section)`
  padding: 60px 20px;
  background-color: #f7f7f7;
`;

const HighlightBox = styled.div`
  max-width: 800px;
  margin: 40px auto;
  border: 1px solid #ddd;
  padding: 30px;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px; /* Space between icon and text */

  h3 {
    color: #333;
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 15px;
  }

  p {
    font-size: 0.9em;
    color: #666;
  }
`;

const HighlightIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #f0f0f0; /* Placeholder for actual icon */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
  color: #555;
`;

const HighlightTextContent = styled.div`
  text-align: left;
  flex: 1;
`;

// --- 5. Content Writing Services We Offer Section (Grid of Services) ---

const ServicesSection = styled(Section)`
  padding: 80px 20px;
`;

const ServiceIntro = styled.p`
  max-width: 800px;
  margin: 0 auto 50px;
  font-size: 1em;
  color: #555;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
`;

const ServiceIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: #f0f0f0; /* Placeholder for icon */
  border-radius: 50%;
  margin-right: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  color: #555;
  flex-shrink: 0; /* Prevent icon from shrinking */
`;

const ServiceContent = styled.div`
  h4 {
    font-size: 1.1em;
    font-weight: bold;
    margin-top: 0;
    margin-bottom: 5px;
  }

  p {
    font-size: 0.85em;
    color: #666;
    margin: 0;
  }
`;

// --- 6. Main App Component ---

const engagementWays = [
  { num: 1, title: "Compelling Visuals", description: "First impressions count! High-quality product images, captivating videos, and visually appealing layouts grab users' attention immediately, making them more likely to explore your offerings." },
  { num: 2, title: "Personalized Recommendations", description: "Tailored product suggestions based on past behavior or expressed preferences keep users engaged and encourage repeat purchases." },
  { num: 3, title: "User Reviews and Ratings", description: "Encouraging user-generated content like reviews and ratings provides valuable social proof, builds trust, and fosters a sense of community, significantly boosting engagement and conversions." },
  { num: 4, title: "Interactive Content", description: "Quizzes, polls, surveys, and interactive product showcases captivate users, gather valuable data, and provide a more dynamic experience, making their journey through your store more memorable and enjoyable." },
  { num: 5, title: "Gamification Strategies", description: "Introduce elements like loyalty programs, rewards, and contests to incentivize users to create an account, make repeat purchases, and share their experiences, turning shopping into a fun and rewarding adventure." },
];

const services = [
  { title: "Business Strategy & Development", description: "We define clear objectives and develop scalable solutions to ensure your e-commerce growth.", icon: 'Icon' },
  { title: "Payment Gateway Integration:", description: "Seamless and secure payment processing options tailored to your business needs.", icon: 'Icon' },
  { title: "Responsive Web Design", description: "Beautiful and functional sites across all devices, ensuring a consistent user experience.", icon: 'Icon' },
  { title: "Inventory Management:", description: "Streamlined inventory tracking, automated updates, and efficient stock control for optimal operations.", icon: 'Icon' },
  { title: "E-commerce Platform Expertise:", description: "We leverage leading platforms like WooCommerce to build powerful and flexible online stores.", icon: 'Icon' },
  { title: "SEO Optimization:", description: "Improve your search engine ranking to attract more organic traffic and potential customers.", icon: 'Icon' },
  { title: "Custom Shopping Carts", description: "Tailored cart experiences designed to boost conversions and reduce abandoned carts.", icon: 'Icon' },
  { title: "Security Measures:", description: "Robust security protocols to protect your data and customers from threats.", icon: 'Icon' },
];


function CustomEcommerce() {
  return (
    <Home>
      <GlobalStyle />
      <HeaderSection>
        <HeaderTitle>
          Transform Your Brand With Custom E-Commerce
        </HeaderTitle>
        <ContactButton href="#">CONTACT NOW</ContactButton>
      </HeaderSection>

      <PageContainer>
        <EngagementSection>
          <SectionTitle>
            5 WAYS TO INCREASE ECOMMERCE USER ENGAGEMENT
          </SectionTitle>
          <FeatureList>
            {engagementWays.map((way) => (
              <FeatureItem key={way.num}>
                <FeatureNumber>{way.num}</FeatureNumber>
                <FeatureContent>
                  <h3>{way.title}</h3>
                  <p>{way.description}</p>
                </FeatureContent>
              </FeatureItem>
            ))}
          </FeatureList>
        </EngagementSection>

        <HighlightSection>
          <SectionTitle>
            Why choose Ghosting Tech?
          </SectionTitle>
          <HighlightBox>
            <HighlightIcon>
              {/* Security Icon SVG/Image */}
            </HighlightIcon>
            <HighlightTextContent>
              <h3>ENHANCED PRIVACY AND SECURITY</h3>
              <p>
                Ghosting Tech provides an advanced set of privacy and security features to protect your website and its users, safeguarding data and ensuring trust.
              </p>
            </HighlightTextContent>
          </HighlightBox>
          <PrimaryButton href="#">Get A Free Strategy</PrimaryButton>
        </HighlightSection>

        <ServicesSection>
          <SectionTitle>
            Content Writing Services We Offer
          </SectionTitle>
          <ServiceIntro>
            At Ghosting Tech, we understand that exceptional content is the cornerstone of a successful online presence. That's why we offer a comprehensive range of content writing services designed to captivate your audience and drive conversions.
          </ServiceIntro>

          <ServiceGrid>
            {services.map((service) => (
              <ServiceItem key={service.title}>
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceContent>
                  <h4>{service.title}</h4>
                  <p>{service.description}</p>
                </ServiceContent>
              </ServiceItem>
            ))}
          </ServiceGrid>
        </ServicesSection>
      </PageContainer>
    </Home>
  );
}

export default CustomEcommerce;

